import { useCallback, useMemo, useRef, useState } from 'react';
import Dexie from 'dexie';
import Papa from 'papaparse';
import { buildDashboardCharts, buildDashboardMetrics, normalizeEVRows } from '../utils/evData';

const CACHE_VERSION = '2026-04-02-v1';
const CSV_FILE_PATH = `${process.env.PUBLIC_URL || ''}/Electric_Vehicle_Population_Data.csv`;
const CACHE_CHUNK_SIZE = 5000;

const db = new Dexie('EVDatabase');
db.version(2).stores({
    evData: 'id, vehicleId, Make, Model, ModelYear, ElectricRange, VehicleType, County, City, State',
    metadata: 'key',
});

function parseCsvFile(filePath) {
    return new Promise((resolve, reject) => {
        Papa.parse(filePath, {
            download: true,
            header: true,
            skipEmptyLines: 'greedy',
            complete: (results) => {
                const fatalError = results.errors?.find((error) => error.code !== 'TooFewFields');

                if (fatalError) {
                    reject(new Error(fatalError.message));
                    return;
                }

                resolve(results.data || []);
            },
            error: (error) => {
                reject(error);
            },
        });
    });
}

async function writeCache(data) {
    await db.transaction('rw', db.evData, db.metadata, async () => {
        await db.evData.clear();

        for (let index = 0; index < data.length; index += CACHE_CHUNK_SIZE) {
            await db.evData.bulkPut(data.slice(index, index + CACHE_CHUNK_SIZE));
        }

        await db.metadata.put({
            key: 'ev-data-cache',
            version: CACHE_VERSION,
            recordCount: data.length,
            updatedAt: new Date().toISOString(),
        });
    });
}

async function readCache() {
    const cacheMetadata = await db.metadata.get('ev-data-cache');

    if (!cacheMetadata || cacheMetadata.version !== CACHE_VERSION || !cacheMetadata.recordCount) {
        return null;
    }

    const cachedRows = await db.evData.toArray();

    if (cachedRows.length !== cacheMetadata.recordCount) {
        return null;
    }

    return cachedRows;
}

export function useEVData() {
    const activeLoadRequest = useRef(null);
    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadCSVData = useCallback(async () => {
        if (activeLoadRequest.current) {
            return activeLoadRequest.current;
        }

        setLoading(true);
        setError('');

        const request = (async () => {
            try {
                const cachedRows = await readCache();

                if (cachedRows?.length) {
                    setRawData(cachedRows);
                    return cachedRows;
                }

                const rawRows = await parseCsvFile(CSV_FILE_PATH);
                const normalizedRows = normalizeEVRows(rawRows);

                if (!normalizedRows.length) {
                    throw new Error('The CSV file was parsed successfully, but no vehicle records were available.');
                }

                await writeCache(normalizedRows);
                setRawData(normalizedRows);
                return normalizedRows;
            } catch (loadError) {
                setError(loadError instanceof Error ? loadError.message : 'Unable to load EV dashboard data.');
                setRawData([]);
                return [];
            } finally {
                activeLoadRequest.current = null;
                setLoading(false);
            }
        })();

        activeLoadRequest.current = request;
        return request;
    }, []);

    const metrics = useMemo(() => buildDashboardMetrics(rawData), [rawData]);
    const chartData = useMemo(() => buildDashboardCharts(rawData), [rawData]);

    return {
        loading,
        error,
        metrics,
        chartData,
        loadCSVData,
    };
}